import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import {
  Gauge, AlertCircle, RefreshCw, Loader2, ChevronDown, ChevronRight, Info,
  TrendingUp, TrendingDown, Minus, Clock,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useMarketRegime, refreshMarketRegime, rebuildMarketRegime } from '../hooks/useMarketRegime';
import {
  signalColor, signalBadge, severityColors, formatValue, formatChange, changeColor,
  seriesFor, definitionsById, referenceLines,
} from './regimeHelpers';

const RANGES = [
  { days: 95,  label: '3M' },
  { days: 185, label: '6M' },
  { days: 370, label: '1Y' },
];

/**
 * Russ's morning checklist, read off market data: five questions (risk
 * appetite, credit, cost of money, fear, real economy) plus his metals
 * ratios. Each indicator carries a rules-based signal; the backend owns the
 * rules and the wording, this page only lays them out.
 */
const MarketRegime = () => {
  const [days, setDays] = useState(RANGES[0].days);
  const [busy, setBusy] = useState(null); // null | 'refresh' | 'rebuild'
  const [notice, setNotice] = useState(null);
  const { data, loading, error, reload } = useMarketRegime(days);

  const snapshot = data?.snapshot;
  const defs = useMemo(() => definitionsById(data?.definitions), [data]);

  const run = async (kind) => {
    setBusy(kind);
    setNotice(null);
    try {
      const res = kind === 'rebuild' ? await rebuildMarketRegime(365) : await refreshMarketRegime();
      const s = res?.summary || {};
      const missing = (s.missingSeries || []).length;
      setNotice(`Wrote ${s.written ?? 0} day(s) through ${s.latestDate || '—'}` +
        `${s.live ? ', including today\'s live quotes' : ''}` +
        `${missing ? `; no data for ${s.missingSeries.join(', ')}` : ''}.`);
      reload();
    } catch (err) {
      setNotice(err?.message || 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Market Regime</h1>
          <p>Russ's morning checklist, read off market data. A rules-based reading of his framework, not his own read.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {snapshot && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              {format(parseISO(snapshot.date), 'EEE MMM d')}
              {snapshot.live ? ' · live' : ' · close'}
              {snapshot.computedAt && ` · updated ${format(parseISO(snapshot.computedAt), 'h:mm a')}`}
            </span>
          )}
          <button className="btn btn-secondary" onClick={() => run('refresh')} disabled={busy !== null}>
            {busy === 'refresh' ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
            Refresh
          </button>
        </div>
      </div>

      {notice && (
        <div className="regime-alert" style={{ background: 'var(--severity-info-bg)', borderLeftColor: 'var(--severity-info-border)' }}>
          {notice}
        </div>
      )}

      {error && (
        <div className="error" style={{ marginBottom: '1rem' }}>
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {loading && !data ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : !snapshot ? (
        <div className="card">
          <div className="empty-state">
            <Gauge size={40} />
            <p>No market-regime snapshot yet.</p>
            <p style={{ fontSize: '0.875rem' }}>
              The timer writes one every 15 minutes during US market hours. To fill in the past year now,
              build history. It fetches about 30 series from Schwab and FRED and takes under a minute.
            </p>
            <button className="btn btn-primary" onClick={() => run('rebuild')} disabled={busy !== null}>
              {busy === 'rebuild' ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
              Build history
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="regime-groups">
            {snapshot.groups.map((g) => (
              <a key={g.id} href={`#regime-${g.id}`} className="regime-group-tile">
                <span className="regime-question">{g.question}</span>
                <span className={`badge ${signalBadge(g.signal)}`}>{g.verdict}</span>
                <span className="regime-counts">
                  <span style={{ color: 'var(--success-color)' }}>{g.positive}▲</span>
                  <span style={{ color: 'var(--danger-color)' }}>{g.negative}▼</span>
                  <span>{g.neutral}–</span>
                </span>
              </a>
            ))}
          </div>

          {snapshot.alerts.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              {snapshot.alerts.map((a) => {
                const c = severityColors(a.severity);
                return (
                  <div key={a.id} className="regime-alert" style={{ background: c.bg, borderLeftColor: c.border }}>
                    <strong>{a.title}.</strong> {a.detail}
                  </div>
                );
              })}
            </div>
          )}

          <div className="filter-buttons" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>History</span>
            {RANGES.map((r) => (
              <button
                key={r.days}
                className={`btn ${days === r.days ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDays(r.days)}
                style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
              >
                {r.label}
              </button>
            ))}
            {loading && <Loader2 size={16} className="spin" />}
          </div>

          {snapshot.groups.map((g) => (
            <GroupSection
              key={g.id}
              group={g}
              readings={snapshot.indicators.filter((r) => r.group === g.id)}
              defs={defs}
              history={data.history}
            />
          ))}

          {snapshot.missingSeries?.length > 0 && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              No data from any provider for: {snapshot.missingSeries.join(', ')}.
            </p>
          )}
        </>
      )}
    </div>
  );
};

const GroupSection = ({ group, readings, defs, history }) => (
  <div className="card" id={`regime-${group.id}`} style={{ marginBottom: '1.25rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
      <h2 className="regime-section-title">{group.question}</h2>
      <span className={`badge ${signalBadge(group.signal)}`}>{group.verdict}</span>
    </div>
    {group.gaps && (
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem' }}>
        <Info size={13} style={{ verticalAlign: '-2px' }} /> {group.gaps}
      </p>
    )}
    <div>
      {readings.map((r) => (
        <IndicatorRow key={r.id} reading={r} def={defs[r.id] || {}} points={seriesFor(history, r.id)} />
      ))}
    </div>
  </div>
);

const TrendIcon = ({ trend }) => {
  if (trend === 'up') return <TrendingUp size={16} />;
  if (trend === 'down') return <TrendingDown size={16} />;
  return <Minus size={16} />;
};

const IndicatorRow = ({ reading, def, points }) => {
  const [open, setOpen] = useState(false);
  const color = signalColor(reading.signal);
  const decimals = def.decimals ?? 4;

  return (
    <div className="regime-row-wrap">
      <button type="button" className="regime-row" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="regime-dot" style={{ background: color }} />
        <span className="regime-label">
          {reading.label}
          {reading.stale && (
            <span className="regime-stale" title="A source lags; this is the last value it published.">
              <Clock size={11} /> {format(parseISO(reading.asOf), 'MMM d')}
            </span>
          )}
          {def.scored === false && <span className="regime-ref">context</span>}
        </span>
        <span className="regime-value">{formatValue(reading.value, decimals)}</span>
        <span className="regime-change" style={{ color: changeColor(reading.change5d, def.polarity) }}>
          <small>5d</small> {formatChange(reading.change5d, reading.changeMode)}
        </span>
        <span className="regime-change" style={{ color: changeColor(reading.change20d, def.polarity) }}>
          <small>20d</small> {formatChange(reading.change20d, reading.changeMode)}
        </span>
        <span className="regime-trend" style={{ color }}><TrendIcon trend={reading.trend} /></span>
        <span className="regime-spark">
          {points.length > 1 && (
            <ResponsiveContainer width="100%" height={32}>
              <LineChart data={points}>
                <YAxis hide domain={['auto', 'auto']} />
                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </span>
        {open ? <ChevronDown size={16} className="regime-chevron" /> : <ChevronRight size={16} className="regime-chevron" />}
      </button>
      {reading.note && <div className="regime-note" style={{ color }}>{reading.note}</div>}

      {open && (
        <div className="regime-detail">
          {def.why && <p>{def.why}</p>}
          <p className="regime-meta">
            <span><strong>Formula</strong> {def.formula}</span>
            {reading.high52w != null && (
              <span><strong>52w</strong> {formatValue(reading.low52w, decimals)} – {formatValue(reading.high52w, decimals)}</span>
            )}
            {reading.sma20 != null && <span><strong>SMA20</strong> {formatValue(reading.sma20, decimals)}</span>}
            {reading.sma50 != null && <span><strong>SMA50</strong> {formatValue(reading.sma50, decimals)}</span>}
            <span><strong>As of</strong> {reading.asOf}</span>
          </p>
          {def.proxyNote && <p className="regime-meta"><Info size={13} style={{ verticalAlign: '-2px' }} /> {def.proxyNote}</p>}
          {points.length > 1 && (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => format(parseISO(d), 'MMM d')}
                  tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                  minTickGap={40}
                />
                <YAxis
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                  tickFormatter={(v) => formatValue(v, Math.min(decimals, 3))}
                  width={64}
                />
                <Tooltip
                  formatter={(v) => [formatValue(v, decimals), reading.label]}
                  labelFormatter={(d) => format(parseISO(d), 'MMM d, yyyy')}
                  contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', fontSize: '0.8125rem' }}
                />
                {referenceLines(reading.id).map((l) => (
                  <ReferenceLine key={l.y} y={l.y} stroke="var(--text-secondary)" strokeDasharray="4 4" ifOverflow="extendDomain"
                    label={{ value: l.label, position: 'insideTopRight', fontSize: 10, fill: 'var(--text-secondary)' }} />
                ))}
                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketRegime;
